import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';

interface Result<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  refetch: () => void;
}

function sameDeps(a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>): boolean {
  return a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
}

/**
 * Минималистичный хук для запроса данных из Supabase. Перезапускается
 * при смене user_id / deps и каждые `pollMs` миллисекунд (если задано).
 * Поллинг ставится на паузу, пока вкладка скрыта (`document.hidden`),
 * и при возвращении во вкладку делается немедленный рефетч.
 */
export function useSupabaseQuery<T>(
  queryFn: () => PromiseLike<{ data: T | null; error: { message: string } | null }>,
  deps: ReadonlyArray<unknown> = [],
  pollMs?: number,
): Result<T> {
  const { userId } = useAuth();
  const [state, setState] = useState<Omit<Result<T>, 'refetch'>>({
    data: null,
    error: null,
    isLoading: true,
  });
  const [tick, setTick] = useState(0);
  // Ключ запроса (userId + deps) — при его смене старые данные не показываем.
  const prevKeyRef = useRef<ReadonlyArray<unknown> | null>(null);
  // Номер последнего запроса — ответы от устаревших запросов игнорируются.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const key = [userId, ...deps];
    const keyChanged = prevKeyRef.current != null && !sameDeps(prevKeyRef.current, key);
    prevKeyRef.current = key;
    const reqId = ++reqIdRef.current;

    if (!userId && import.meta.env.PROD) {
      setState({ data: null, error: 'Не авторизован', isLoading: false });
      return;
    }
    if (keyChanged) {
      setState({ data: null, error: null, isLoading: true });
    } else {
      setState((s) => ({ ...s, isLoading: s.data == null && s.error == null }));
    }
    const run = async (): Promise<void> => {
      let data: T | null = null;
      let error: { message: string } | null = null;
      try {
        ({ data, error } = await queryFn());
      } catch (err) {
        // Сетевой сбой/исключение в queryFn — иначе спиннер висел бы бесконечно.
        error = { message: err instanceof Error ? err.message : String(err) };
      }
      if (reqId !== reqIdRef.current) return;
      setState((prev) => ({
        data: error ? prev.data : data ?? null,
        error: error?.message ?? null,
        isLoading: false,
      }));
    };
    void run();

    if (pollMs && pollMs > 0) {
      const id = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        setTick((t) => t + 1);
      }, pollMs);
      return () => clearInterval(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tick, ...deps]);

  // Возврат во вкладку — сразу обновляем данные (только для поллящихся запросов).
  useEffect(() => {
    if (!pollMs || pollMs <= 0 || typeof document === 'undefined') return;
    const onVisibility = (): void => {
      if (!document.hidden) setTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [pollMs]);

  // Размонтирование — инвалидируем текущий запрос.
  useEffect(() => () => {
    reqIdRef.current += 1;
  }, []);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}

/** Обёртка над `supabase` чтобы не импортировать его в каждой странице. */
export const sb = supabase;
